import { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import PageLayout, { useOpenAiSearch } from '../components/PageLayout';
import ArticleIcon, { NEWS_ICON_FALLBACK } from '../components/ArticleIcon';
import PageHeader from '../components/ui/PageHeader';
import PageMain from '../components/ui/PageMain';
import PageBody from '../components/ui/PageBody';
import { getNewsList } from '../api';
import { MvLandscape } from '../components/brand/MvLandscape';
import ArticleLink from '../components/ArticleLink';

/*
 * The carousel is an endless belt: one set of tiles, repeated, slid left by
 * exactly one set's width and then restarted - the restart lands on identical
 * tiles, so nothing jumps. The set has to be at least as wide as the screen
 * (or the belt's end would show before the restart), so the page first works
 * out how many tiles fill the width and asks the server for that many news.
 */
const TILE_STEP_PX = 190 + 18;      // tile width + gap at the widest layout
const MIN_TILES = 6;
const MAX_TILES = 16;
const BELT_SPEED_PX_PER_S = 31;     // the speed the fixed 40 s loop used to give

function tilesForWidth(width) {
  const fills = Math.ceil(width / TILE_STEP_PX) + 1;

  return Math.min(MAX_TILES, Math.max(MIN_TILES, fills));
}

/**
 * The home page's assistant field is a doorway: the first letter typed opens
 * the assistant sheet with it (as does "Fragen"), and the search and the chat
 * go on there, in one fixed place, instead of growing the page under the
 * reader. Rendered inside PageLayout, whose sheet it opens.
 */
function AssistantLauncher() {
  const { t } = useTranslation();
  const openAiSearch = useOpenAiSearch();

  return (
    <form
      className="menu-ai-form"
      onSubmit={(event) => {
        event.preventDefault();
        openAiSearch();
      }}
    >
      <label
        className="sr-only"
        htmlFor="menuAiInput"
      >
        {t('home.aiInputLabel')}
      </label>

      <input
        id="menuAiInput"
        value=""
        onChange={(event) => openAiSearch(event.target.value)}
        autoComplete="off"
        placeholder={t('home.aiInputPlaceholder')}
      />

      <button type="submit" className="ui-button account-primary-button">
        {t('home.aiSubmit')}
        <i className="fa-solid fa-arrow-right" />
      </button>
    </form>
  );
}

function Home() {
  const { t } = useTranslation();
  const [news, setNews] = useState([]);
  const trackRef = useRef(null);

  useEffect(() => {
    let cancelled = false;

    async function loadNews() {
      try {
        const data = await getNewsList(tilesForWidth(window.innerWidth));

        if (!cancelled) {
          setNews((data ?? []).filter((item) => !item.is_hidden));
        }
      } catch (error) {
        console.error('Failed to load news for home carousel:', error);
      }
    }

    loadNews();

    return () => {
      cancelled = true;
    };
  }, []);

  const initialPreview = {
    titleKey: 'home.previewTitle',
    descriptionKey: 'home.previewDescription',
    icon: 'fa-solid fa-circle-info',
  };

  const [preview, setPreview] = useState(initialPreview);
  const carouselItems = news.map((item) => ({
    type: 'news',
    to: `/news/${item.article_id}`,
    article: item,
    title: item.title,
    description: item.summary,
    icon: item.icon,
  }));

  // Fewer news than the screen is wide (a fresh installation, a narrow
  // window): repeat the set until one copy alone covers the screen, so the
  // seam is never in view. Then one more copy to slide into.
  const setSize = carouselItems.length;
  const viewportWidth =
    typeof window === 'undefined' ? 0 : window.innerWidth;
  const perCopy = setSize > 0 ? Math.max(1, Math.ceil(tilesForWidth(viewportWidth) / setSize)) : 0;
  const copies = perCopy + 1;
  const carouselSections = Array.from(
    { length: copies * setSize },
    (_, index) => carouselItems[index % setSize],
  );
  const loopSize = perCopy * setSize;

  // Measure the belt after it is laid out and keep it measured on resize:
  // the shift is the distance between a tile and its next repeat, and the
  // duration follows the shift so the speed stays the same however many
  // tiles there are.
  useLayoutEffect(() => {
    const track = trackRef.current;

    if (!track || loopSize === 0) {
      return undefined;
    }

    function measure() {
      const first = track.children[0];
      const repeat = track.children[loopSize];

      if (!first || !repeat) {
        return;
      }

      const shift = repeat.offsetLeft - first.offsetLeft;

      track.style.setProperty('--carousel-shift', `${shift}px`);
      track.style.setProperty(
        '--carousel-duration',
        `${Math.round(shift / BELT_SPEED_PX_PER_S)}s`,
      );
    }

    measure();

    const observer = new ResizeObserver(measure);
    observer.observe(track);

    return () => observer.disconnect();
  }, [loopSize, copies]);

  return (
    <PageLayout showAiSearch>
      <PageMain className="home-page">
        <PageHeader
          title={t('home.pageTitle')}
          titleId="home-page-title"
          description={t('home.pageDescription')}
          landscape={false}
        />

        <PageBody
          className="home-body"
          aria-labelledby="home-page-title"
        >

          <section className="menu-carousel-section">
            <div
              className="menu-carousel"
              aria-label={t('home.carouselAriaLabel')}
            >
              <MvLandscape
                className="home-landscape"
                bands={[
                  { color: 'var(--mv-eisblau-50)', top: 8, amplitude: 40 },
                  { color: 'var(--mv-himmelblau)', top: 40, amplitude: 34, flip: true },
                  { color: 'var(--mv-wasserblau)', top: 66, amplitude: 26 },
                ]}
              />

              <div className="menu-carousel-track" ref={trackRef}>
                {carouselSections.map((item, index) => (
                  <ArticleLink
                    key={`${item.type}-${item.to}-${index}`}
                    article={item.article}
                    className="menu-tile"
                    onMouseEnter={() => setPreview(item)}
                    onFocus={() => setPreview(item)}
                  >
                    <ArticleIcon icon={item.icon} fallback={NEWS_ICON_FALLBACK} seed={item.title} alt="" />

                    <span>
                      {item.type === 'section'
                        ? t(item.titleKey)
                        : item.title}
                    </span>
                  </ArticleLink>
                ))}
              </div>
            </div>

            <article
              className="menu-tile-preview"
              aria-live="polite"
            >
              <div className="menu-preview-icon">
                <ArticleIcon icon={preview.icon} fallback={NEWS_ICON_FALLBACK} seed={preview.title} alt="" />
              </div>

              <div>
                {/* Sections carry translation keys, news items carry their own
                    text; the panel shows whichever the hovered tile has. */}
                <h3>
                  {preview.titleKey
                    ? t(preview.titleKey)
                    : preview.title}
                </h3>

                <p>
                  {preview.descriptionKey
                    ? t(preview.descriptionKey)
                    : preview.description}
                </p>
              </div>
            </article>
          </section>

          <section
            className="menu-ai-chat"
            aria-labelledby="menu-ai-title"
          >
            <div className="menu-ai-intro">
              <div className="menu-ai-icon">
                <i className="fa-solid fa-wand-magic-sparkles" />
              </div>

              <div>
                <p className="menu-eyebrow">
                  {t('home.aiEyebrow')}
                </p>

                <h2 id="menu-ai-title">
                  {t('home.aiQuestion')}
                </h2>

                <p>
                  {t('home.aiDescription')}
                </p>
              </div>
            </div>

            <div
              className="menu-ai-example"
              aria-label={t('home.aiExampleAriaLabel')}
            >
              <strong>
                {t('home.aiExampleAuthor')}
              </strong>

              <p>
                {t('home.aiExampleText')}
              </p>
            </div>

            <AssistantLauncher />
          </section>

        </PageBody>
      </PageMain>
    </PageLayout>
  );
}

export default Home;
